import { Button, FluentProvider, webLightTheme } from '@fluentui/react-components';
import {
    CalendarClock20Regular,
    CalendarEdit20Regular,
    CloudArrowUp20Regular,
    Delete20Regular,
    GlobeSurface20Regular,
    Info20Regular,
    Notebook20Regular,
    Open20Regular,
} from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import environmentCardPreviewImage_Dark from '../../../content/images/environments-preview_dark.jpg';
import environmentCardPreviewImage_Light from '../../../content/images/environments-preview_light.jpg';
import { useActionCreator } from '../../../hooks/action-creator';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { Failure } from '../../../models/common';
import { Tenant } from '../../../models/resource-manager';
import { IndexedPayload } from '../../../redux/actions/core-actions';
import { loadEnvironmentOperationsAndLogs } from '../../../redux/actions/environment-operation/environment-operation-action-creators';
import { getSelectedTenant } from '../../../redux/selector/tenant-selector';
import { openInNewWindow } from '../../../utilities/browser';
import { isNotUndefinedOrWhiteSpace } from '../../../utilities/string';
import { ConfirmationDialogProperties } from '../../common/confirmation-dialog/contexts';
import { MetadataItemViewModel } from '../../common/metadata/models';
import { RedeployEnvironmentPanelContextProperties } from '../../redeploy-environment-panel/contexts';
import ResourceCard from '../../resource-card/resource-card';
import { ResourceCardAction } from '../../resource-card/resource-card-action';
import { ChangeEnvironmentExpirationDialogProperties } from '../change-expiration-dialog/contexts';
import { DeploymentLogsPanelContextProperties } from '../deployment-logs-panel/context';
import { EnvironmentDetailsPanelContextProperties } from '../environment-details-panel/context';
import { ForceDeleteDialogProperties } from '../force-delete-dialog/contexts';
import { EnvironmentViewModel } from '../models';
import { RedeployEnvironmentDialogProperties } from '../redeploy-environment-dialog/contexts';
import AzureProductImage from './azure-product-image';
import {
    EnvironmentStateAnnouncement,
    EnvironmentTypeMetadata,
    ExpirationDateOffsetMetadata,
    getEnvironmentStateAnnouncementProps,
    getEnvironmentStateMessage,
    getShimmeredEnvironmentMetadata,
} from './environment-metadata';
import {
    getAreActionsDisabled,
    getEnvironmentBannerViewModel,
    getMetadata,
    getResourceCardError,
    getSecondaryActions,
    getUseTranslucentPreviewImage,
} from './selectors';

interface EnvironmentCardProps {
    environment: EnvironmentViewModel;
    onDeleteSubmitted: (resourceId: string, force: boolean) => void;
    onSeeErrorDetails: (failure: Failure) => void;
    onOpenConfirmationDialog: (properties: ConfirmationDialogProperties) => void;
    onOpenForceDeleteDialog: (properties: ForceDeleteDialogProperties) => void;
    onOpenRedeployDialog: (properties: RedeployEnvironmentDialogProperties) => void;
    onOpenRedeployEnvironmentPanel: (properties: RedeployEnvironmentPanelContextProperties) => void;
    onClearRedeployEnvironmentFailure: () => void;
    onOpenChangeEnvironmentExpirationDialog: (properties: ChangeEnvironmentExpirationDialogProperties) => void;
    onOpenEnvironmentDetailsPanel: (props: EnvironmentDetailsPanelContextProperties) => void;
    onOpenDeploymentLogsPanel: (props: DeploymentLogsPanelContextProperties) => void;
}

interface EnvironmentCardComponentProps extends EnvironmentCardProps {
    selectedTenant?: Tenant;
    loadOperationsAndLogs: (payload: IndexedPayload) => void;
}

const messages = defineMessages({
    deleteDialogPrimaryButtonAriaLabel: {
        id: 'EnvironmentCard_DeleteDialogPrimaryButton_AriaLabel',
        defaultMessage: 'Delete the environment',
        description: 'Aria label for the submit button to confirm deleting the environment',
    },
    deleteDialogPrimaryButtonText: {
        id: 'EnvironmentCard_DeleteDialogPrimaryButton_Text',
        defaultMessage: 'Delete',
        description: 'Confirmation button to delete the environment',
    },
    deleteDialogSubText: {
        id: 'EnvironmentCard_DeleteDialogSubText_Text',
        defaultMessage:
            'This environment and all its data will be permanently removed. Are you sure you want to delete {name}?',
        description:
            'Informs the user about the effects of deleting the environment and confirms. {name} should not be localized, it is the name of the environment.',
    },
    deleteDialogTitle: {
        id: 'EnvironmentCard_DeleteDialogTitle_Text',
        defaultMessage: 'Delete environment',
        description: 'Header text for environment delete dialog',
    },
    deleteMenuItemAriaLabel: {
        id: 'EnvironmentCard_DeleteMenuItem_AriaLabel',
        defaultMessage: 'Delete environment',
        description: 'Aria label for "Delete" contextual menu item in environment card\'s settings button.',
    },
    forceDeleteDialogSubText: {
        id: 'EnvironmentCard_ForceDeleteDialogSubText_Text',
        defaultMessage:
            'The environment definition used to create this environment is no longer available. Force delete is a best-effort delete that will delete the environment, its deployment resource group, and all resources in the deployment resource group. Any resources created outside the deployment resource group will be orphaned.',
        description: 'Informs the user about the effects of force deleting the environment and confirms.',
    },
    environmentTypeIconAriaLabel: {
        id: 'EnvironmentCard_EnvironmentTypeIcon_AriaLabel',
        defaultMessage: 'Environment type',
        description: 'Aria label for the environment type icon in the environment card',
    },
    expirationDateIconAriaLabel: {
        id: 'EnvironmentCard_ExpirationDateIcon_AriaLabel',
        defaultMessage: 'Expiration date',
        description: 'Aria label for the expiration date icon in the environment card',
    },
    redeployMenuItemAriaLabel: {
        id: 'EnvironmentCard_RedeployMenuItem_AriaLabel',
        defaultMessage: 'Redeploy environment',
        description: 'Aria label for "Redeploy" contextual menu item in environment card\'s settings button.',
    },
    redeployDialogSubText: {
        id: 'EnvironmentCard_RedeployDialogSubText_Text',
        defaultMessage:
            'The environment definition {environmentDefinitionName} has no parameters, and will be redeployed immediately.',
        description:
            'Informs the user that their environment will be redeployed. {environmentDefinitionName} should not be localized, it is the name of the environment definition.',
    },
    redeployDialogTitle: {
        id: 'EnvironmentCard_RedeployDialogTitle_Text',
        defaultMessage: 'Redeploy {environmentName}',
        description:
            'Header text for environment redeploy dialog. {environmentName} should not be localized, it is the name of the environment to be redeployed.',
    },
    changeExpirationDateMenuItemAriaLabel: {
        id: 'EnvironmentCard_ChangeExpirationMenuItem_AriaLabel',
        defaultMessage: 'Change expiration',
        description: 'Aria label for "Change expiration" contextual menu item in environment card\'s settings button.',
    },
    moreInfoMenuItemAriaLabel: {
        id: 'EnvironmentCard_MoreInfoMenuItem_AriaLabel',
        defaultMessage: 'More info',
        description: 'Aria label for "More info" contextual menu item in environment card\'s settings button.',
    },
    deploymentLogsItemAriaLabel: {
        id: 'EnvironmentCard_DeploymentLogsItem_AriaLabel',
        defaultMessage: 'Deployment logs',
        description: 'Aria label for "Deployment logs" contextual menu in environment card\'s settings button.',
    },
    environmentResourcesButtonAriaLabel: {
        id: 'EnvironmentResources_Button_AriaLabel',
        defaultMessage: '{environmentName} environment resources',
        description: 'Aria label for Environment Resources Link Button. {environmentName} should not be localized.',
    },
});

export const EnvironmentCardComponent: React.FC<EnvironmentCardComponentProps> = React.memo(
    (props: EnvironmentCardComponentProps) => {
        const {
            environment,
            onDeleteSubmitted,
            onSeeErrorDetails,
            selectedTenant,
            onOpenConfirmationDialog,
            onOpenForceDeleteDialog,
            onOpenRedeployEnvironmentPanel,
            onClearRedeployEnvironmentFailure,
            onOpenRedeployDialog,
            onOpenChangeEnvironmentExpirationDialog,
            onOpenEnvironmentDetailsPanel,
            onOpenDeploymentLogsPanel,
            loadOperationsAndLogs,
        } = props;

        const {
            resource,
            state,
            projectName: projectResourceName,
            projectDisplayName,
            environmentDefinition,
            isCardContentReady,
            failureOnEnvironment,
            expirationDateOffset,
            devCenterName,
        } = environment;
        const { failure } = failureOnEnvironment ?? {};
        const { catalogName, environmentDefinitionName, environmentType, expirationDate, name, resourceGroupId, uri } =
            resource;
        const environmentDefinitionParameters = environmentDefinition?.parameters ?? [];

        // Intl hooks
        const { formatMessage } = useIntl();

        // Theme hooks
        const theme = useCurrentFluent2Theme();

        // Note: putting these memoizers here since they're used within callbacks
        const resourceCardError = React.useMemo(
            () => getResourceCardError(failureOnEnvironment, state),
            [failureOnEnvironment, state]
        );

        const hasFailure = !!failure;

        const isEnvironmentDefinitionReady = !!environmentDefinition;
        const isShimmered = !isCardContentReady;

        const isForceDeleteRequired = React.useMemo(
            () => isCardContentReady && !isEnvironmentDefinitionReady,
            [isCardContentReady, isEnvironmentDefinitionReady]
        );

        // Callback hooks
        const onDeleteDialogPrimaryButtonClicked = React.useCallback(() => {
            onDeleteSubmitted(uri, false);
        }, [onDeleteSubmitted, uri]);

        const onForceDeleteDialogPrimaryButtonClicked = React.useCallback(() => {
            onDeleteSubmitted(uri, true);
        }, [onDeleteSubmitted, uri]);

        const onSeeErrorDetailsClicked = React.useCallback(() => {
            if (!hasFailure) {
                return;
            }

            onSeeErrorDetails(failure);
        }, [hasFailure, failure, onSeeErrorDetails]);

        // Memoized data
        const areActionsDisabled = React.useMemo(() => getAreActionsDisabled(state), [state]);
        const deleteDialogSubTextValues = React.useMemo(() => ({ name }), [name]);
        const useTranslucentPreviewImage = React.useMemo(() => getUseTranslucentPreviewImage(state), [state]);
        const hasParameters = environmentDefinitionParameters.length > 0;

        const projectName = React.useMemo(
            () => (isNotUndefinedOrWhiteSpace(projectDisplayName) ? projectDisplayName : projectResourceName),
            [projectDisplayName, projectResourceName]
        );

        // Resource card actions
        // NOTE: Icon names used below ('Delete', 'Redeploy', etc.) can be found at https://github.com/microsoft/fluentui-system-icons/blob/main/icons_regular.md

        // Delete action
        const onDeleteMenuItemClicked = React.useCallback(() => {
            if (isForceDeleteRequired) {
                onOpenForceDeleteDialog({
                    onPrimaryButtonSubmitted: onForceDeleteDialogPrimaryButtonClicked,
                    primaryButtonAriaLabel: formatMessage(messages.deleteDialogPrimaryButtonAriaLabel),
                    primaryButtonText: formatMessage(messages.deleteDialogPrimaryButtonText),
                    subText: formatMessage(messages.forceDeleteDialogSubText),
                    title: formatMessage(messages.deleteDialogTitle),
                });
            } else {
                onOpenConfirmationDialog({
                    onPrimaryButtonSubmitted: onDeleteDialogPrimaryButtonClicked,
                    primaryButtonAriaLabel: formatMessage(messages.deleteDialogPrimaryButtonAriaLabel),
                    primaryButtonText: formatMessage(messages.deleteDialogPrimaryButtonText),
                    subText: formatMessage(messages.deleteDialogSubText, deleteDialogSubTextValues),
                    title: formatMessage(messages.deleteDialogTitle),
                });
            }
        }, [
            onOpenConfirmationDialog,
            onOpenForceDeleteDialog,
            onDeleteDialogPrimaryButtonClicked,
            onForceDeleteDialogPrimaryButtonClicked,
            deleteDialogSubTextValues,
            formatMessage,
            isForceDeleteRequired,
        ]);

        const deleteMenuItem = React.useMemo(
            () => (
                <ResourceCardAction
                    ariaLabel={formatMessage(messages.deleteMenuItemAriaLabel)}
                    onClick={onDeleteMenuItemClicked}
                    icon={<Delete20Regular />}
                    key="delete"
                >
                    <FormattedMessage
                        id="EnvironmentCard_DeleteMenuItem_Text"
                        defaultMessage="Delete"
                        description='Text for "Delete" contextual menu item in environment cards settings button.'
                    />
                </ResourceCardAction>
            ),
            [formatMessage, onDeleteMenuItemClicked]
        );

        // Redeploy action
        const onOpenRedeployConfirmationDialog = React.useCallback(() => {
            onOpenRedeployDialog({
                environment: resource,
            });
        }, [onOpenRedeployDialog, resource]);

        const onRedeployMenuItemClicked = React.useCallback(() => {
            onClearRedeployEnvironmentFailure();

            if (hasParameters) {
                onOpenRedeployEnvironmentPanel({ environment });
            } else {
                onOpenRedeployConfirmationDialog();
            }
        }, [
            onClearRedeployEnvironmentFailure,
            hasParameters,
            onOpenRedeployEnvironmentPanel,
            onOpenRedeployConfirmationDialog,
            environment,
        ]);

        const redeployMenuItem = React.useMemo(
            () => (
                <ResourceCardAction
                    ariaLabel={formatMessage(messages.redeployMenuItemAriaLabel)}
                    onClick={onRedeployMenuItemClicked}
                    icon={<CloudArrowUp20Regular />}
                    key="deploy"
                >
                    <FormattedMessage
                        id="EnvironmentCard_RedeployMenuItem_Text"
                        defaultMessage="Redeploy"
                        description='Text for "Redeploy" contextual menu item in environment cards settings button.'
                    />
                </ResourceCardAction>
            ),
            [formatMessage, onRedeployMenuItemClicked]
        );

        // Change delete expiration action
        const onChangeEnvironmentExpirationMenuItemItemClicked = React.useCallback(() => {
            onOpenChangeEnvironmentExpirationDialog({ environment: resource });
        }, [onOpenChangeEnvironmentExpirationDialog, resource]);

        const changeExpirationMenuItem = React.useMemo(
            () => (
                <ResourceCardAction
                    ariaLabel={formatMessage(messages.changeExpirationDateMenuItemAriaLabel)}
                    onClick={onChangeEnvironmentExpirationMenuItemItemClicked}
                    icon={<CalendarEdit20Regular />}
                    key="changeExpiration"
                >
                    <FormattedMessage
                        id="EnvironmentCard_ChangeExpirationMenuItem_Text"
                        defaultMessage="Change expiration"
                        description='Text for "Change expiration" contextual menu item in environment cards settings button.'
                    />
                </ResourceCardAction>
            ),
            [formatMessage, onChangeEnvironmentExpirationMenuItemItemClicked]
        );

        // Show more info action
        const onMoreInfoMenuItemClicked = React.useCallback(() => {
            onOpenEnvironmentDetailsPanel({
                devCenterName,
                projectName,
                expirationDate,
                catalogName,
                environmentDefinitionName,
                name,
            });
        }, [
            onOpenEnvironmentDetailsPanel,
            expirationDate,
            devCenterName,
            projectName,
            catalogName,
            environmentDefinitionName,
            name,
        ]);

        const moreInfoMenuItem = React.useMemo(
            () => (
                <ResourceCardAction
                    ariaLabel={formatMessage(messages.moreInfoMenuItemAriaLabel)}
                    onClick={onMoreInfoMenuItemClicked}
                    icon={<Info20Regular />}
                    key="moreInfo"
                >
                    <FormattedMessage
                        id="EnvironmentCard_MoreInfoMenuItem_Text"
                        defaultMessage="More info"
                        description='Text for "More info" contextual menu item in environment card settings button.'
                    />
                </ResourceCardAction>
            ),
            [formatMessage, onMoreInfoMenuItemClicked]
        );

        // Show deployment logs action
        const onDeploymentLogsItemClicked = React.useCallback(() => {
            // Dispatch actions to call APIs for operations and logs every time user click on this menu item
            loadOperationsAndLogs({ id: uri });
            onOpenDeploymentLogsPanel({ name, uri });
        }, [loadOperationsAndLogs, onOpenDeploymentLogsPanel, name, uri]);

        const deploymentLogsItem = React.useMemo(
            () => (
                <ResourceCardAction
                    ariaLabel={formatMessage(messages.deploymentLogsItemAriaLabel)}
                    onClick={onDeploymentLogsItemClicked}
                    icon={<Notebook20Regular />}
                    key="deploymentLogs"
                >
                    <FormattedMessage
                        id="EnvironmentCard_DeploymentLogsMenuItem_Text"
                        defaultMessage="Deployment logs"
                        description='Text for "Deployment logs" contextual menu item in environment card settings button.'
                    />
                </ResourceCardAction>
            ),
            [formatMessage, onDeploymentLogsItemClicked]
        );

        const secondaryActions = React.useMemo(
            () =>
                getSecondaryActions(
                    deleteMenuItem,
                    redeployMenuItem,
                    changeExpirationMenuItem,
                    state,
                    isEnvironmentDefinitionReady,
                    moreInfoMenuItem,
                    deploymentLogsItem
                ),
            [
                deleteMenuItem,
                redeployMenuItem,
                changeExpirationMenuItem,
                state,
                isEnvironmentDefinitionReady,
                moreInfoMenuItem,
                deploymentLogsItem,
            ]
        );

        //Primary action button
        const ariaLabelValues = React.useMemo(() => ({ environmentName: name }), [name]);

        const viewResourcesLink = React.useMemo(
            () => `https://portal.azure.com/#@${selectedTenant?.defaultDomain}/resource${resourceGroupId}/overview`,
            [selectedTenant, resourceGroupId]
        );
        const onViewResourcesClicked = React.useCallback(
            (): void => openInNewWindow(viewResourcesLink),
            [viewResourcesLink]
        );

        const viewResourceLinkButton = React.useMemo(
            () => (
                <Button
                    aria-label={formatMessage(messages.environmentResourcesButtonAriaLabel, ariaLabelValues)}
                    onClick={onViewResourcesClicked}
                    icon={<Open20Regular />}
                >
                    <FormattedMessage
                        id="EnvironmentCard_ResourceLinkItem_Text"
                        defaultMessage="View resources"
                        description="Button to open external environment resources link."
                    />
                </Button>
            ),
            [onViewResourcesClicked, ariaLabelValues, formatMessage]
        );

        // Resource card metadata
        const environmentTypeMetadata: MetadataItemViewModel = React.useMemo(
            () => ({
                description: formatMessage(messages.environmentTypeIconAriaLabel),
                icon: <GlobeSurface20Regular />,
                key: 'environment-type',
                primary: false,
                value: <EnvironmentTypeMetadata environmentType={environmentType} />,
            }),
            [environmentType, formatMessage]
        );

        const expirationDateMetadata: MetadataItemViewModel | undefined = React.useMemo(
            () =>
                !!expirationDateOffset?.offset && !!expirationDateOffset?.unit
                    ? {
                          description: formatMessage(messages.expirationDateIconAriaLabel),
                          icon: <CalendarClock20Regular />,
                          key: 'expiration-date',
                          primary: false,
                          value: <ExpirationDateOffsetMetadata expirationDateOffset={expirationDateOffset} />,
                      }
                    : undefined,
            [expirationDateOffset, formatMessage]
        );

        const stateMetadata: MetadataItemViewModel | undefined = React.useMemo(() => {
            const stateMessage = getEnvironmentStateMessage(state);

            return !!stateMessage
                ? {
                      ...stateMessage,
                      description: formatMessage(stateMessage.description),
                  }
                : undefined;
        }, [state, formatMessage]);
        const stateAnnouncementProps = React.useMemo(
            () => getEnvironmentStateAnnouncementProps(state, isCardContentReady, formatMessage),
            [formatMessage, isCardContentReady, state]
        );

        const metadata = React.useMemo(
            () => getMetadata(environmentTypeMetadata, expirationDateMetadata, stateMetadata, state),
            [environmentTypeMetadata, expirationDateMetadata, stateMetadata, state]
        );

        const hasResourceGroup = !!resourceGroupId;
        const shimmeredEnvironmentMetadata = React.useMemo(
            () => getShimmeredEnvironmentMetadata(hasResourceGroup),
            [hasResourceGroup]
        );

        const cardBanner = React.useMemo(
            () => getEnvironmentBannerViewModel(resourceCardError, hasFailure ? onSeeErrorDetailsClicked : undefined),
            [resourceCardError, onSeeErrorDetailsClicked, hasFailure]
        );

        return (
            <FluentProvider theme={theme}>
                <EnvironmentStateAnnouncement {...stateAnnouncementProps} />
                <ResourceCard
                    areSecondaryActionsDisabled={areActionsDisabled}
                    metadata={metadata}
                    name={name}
                    productIcon={<AzureProductImage />}
                    previewImageSrc={
                        theme === webLightTheme ? environmentCardPreviewImage_Light : environmentCardPreviewImage_Dark
                    }
                    projectName={projectName}
                    secondaryActions={secondaryActions}
                    footerContent={viewResourceLinkButton}
                    useTranslucentPreviewImage={useTranslucentPreviewImage}
                    displayStateAtTheBottom
                    isShimmered={isShimmered}
                    shimmeredMetadata={shimmeredEnvironmentMetadata}
                    cardBanner={cardBanner}
                />
            </FluentProvider>
        );
    }
);

export const EnvironmentCardContainer: React.FC<EnvironmentCardProps> = (props: EnvironmentCardProps) => {
    // Application state hooks
    const selectedTenant = useSelector(getSelectedTenant);

    // Action hooks
    const loadOperationsAndLogs = useActionCreator(loadEnvironmentOperationsAndLogs);

    return (
        <EnvironmentCardComponent
            {...props}
            selectedTenant={selectedTenant}
            loadOperationsAndLogs={loadOperationsAndLogs}
        />
    );
};

export default EnvironmentCardContainer;
